import type { MutableRefObject } from 'react';
import { useState, useLayoutEffect, useRef } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

interface ElementSize {
  width: number;
  height: number;
}

function getSize(element: HTMLElement | null): ElementSize {
  if (element) {
    return { width: element.offsetWidth, height: element.offsetHeight };
  }
  return { width: 0, height: 0 };
}

function useSize(ref: MutableRefObject<HTMLElement | null>): ElementSize {
  const [size, setSize] = useState(() => getSize(ref.current));

  useLayoutEffect(() => {
    if (ref.current) {
      const observer = new ResizeObserver(() => {
        // use requestAnimationFrame to avoid `ResizeObserver loop` errors (https://stackoverflow.com/a/50387233)
        requestAnimationFrame(() => {
          setSize(getSize(ref.current));
        });
      });
      observer.observe(ref.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [ref.current]);

  return size;
}

function useOnResize(
  ref: MutableRefObject<HTMLElement | null>,
  onResize: (size: ElementSize) => void
): void {
  const handleResize = useRef(onResize);
  handleResize.current = onResize;

  useLayoutEffect(() => {
    if (ref.current) {
      const observer = new ResizeObserver(() => {
        // use requestAnimationFrame to avoid `ResizeObserver loop` errors (https://stackoverflow.com/a/50387233)
        requestAnimationFrame(() => {
          handleResize.current(getSize(ref.current));
        });
      });
      observer.observe(ref.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [ref.current]);
}

export { useSize, useOnResize };
